export type ImportHeaderField = {
  field: string;
  aliases?: readonly string[];
};

export type HeaderMatchMethod = 'normalized' | 'alias' | 'distance';

export type HeaderMatch = {
  /** File header to field. */
  mapping: Record<string, string>;
  unmatched: string[];
  /** File header to every field within reach of it. */
  ambiguous: Record<string, string[]>;
  changed: { from: string; to: string; how: HeaderMatchMethod }[];
};

export type MatchHeadersOptions = {
  maxDistanceRatio?: number;
};

const DEFAULT_MAX_DISTANCE_RATIO = 0.2;
const MIN_FUZZY_HEADER_LENGTH = 5;
const METHOD_PRECEDENCE: Record<HeaderMatchMethod, number> = {
  alias: 1,
  distance: 2,
  normalized: 0,
};
const UMLAUT_SPELLINGS: Record<string, string> = {
  ß: 'ss',
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
};

/**
 * Reduces a header to the part people agree on: case, separators, dots,
 * umlauts and a single plural `s` are dropped, so `Cost-Centers` and
 * `costcenter` meet, as do `Längengrad` and `laengengrad`.
 */
export function normalizeHeader(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replaceAll(/[\s_.-]/g, '')
    .replaceAll(/[äöüß]/g, (umlaut) => UMLAUT_SPELLINGS[umlaut])
    .replace(/s$/, '');
}

export function levenshtein(a: string, b: string): number {
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);

  for (let i = 0; i < a.length; i++) {
    const current = [i + 1];
    for (let j = 0; j < b.length; j++) {
      current.push(
        Math.min(
          previous[j + 1] + 1,
          current[j] + 1,
          previous[j] + (a[i] === b[j] ? 0 : 1),
        ),
      );
    }
    previous = current;
  }

  return previous[b.length];
}

type NormalizedField = {
  field: string;
  key: string;
  aliases: string[];
};

type Candidates = { fields: string[]; how: HeaderMatchMethod };

function distanceRatio(a: string, b: string): number {
  return levenshtein(a, b) / Math.max(a.length, b.length);
}

function findCandidates(
  header: string,
  fields: readonly NormalizedField[],
  maxDistanceRatio: number,
): Candidates {
  const byKey = fields.filter(({ key }) => key === header);
  if (byKey.length > 0) {
    return { fields: byKey.map(({ field }) => field), how: 'normalized' };
  }

  const byAlias = fields.filter(({ aliases }) => aliases.includes(header));
  if (byAlias.length > 0) {
    return { fields: byAlias.map(({ field }) => field), how: 'alias' };
  }

  const byDistance =
    header.length < MIN_FUZZY_HEADER_LENGTH
      ? []
      : fields.filter(({ key, aliases }) =>
          [key, ...aliases].some(
            (candidate) => distanceRatio(header, candidate) <= maxDistanceRatio,
          ),
        );
  return { fields: byDistance.map(({ field }) => field), how: 'distance' };
}

/**
 * Hands each field to one header, stronger methods first and file order
 * within a method, and returns per header the fields still open to it.
 */
function claimFields(candidates: readonly Candidates[]): string[][] {
  const claimedFields = new Set<string>();
  const openFields = candidates.map(() => [] as string[]);
  const byPrecedence = candidates
    .map((_, index) => index)
    .sort(
      (a, b) =>
        METHOD_PRECEDENCE[candidates[a].how] -
        METHOD_PRECEDENCE[candidates[b].how],
    );

  for (const index of byPrecedence) {
    const open = candidates[index].fields.filter(
      (field) => !claimedFields.has(field),
    );
    if (open.length === 1) {
      claimedFields.add(open[0]);
    }
    openFields[index] = open;
  }

  return openFields;
}

/**
 * Maps the headers of an uploaded file onto the fields an import expects:
 * normalized equality first, then aliases, then a Levenshtein distance of at
 * most `maxDistanceRatio` of the longer normalized string. A field goes to the
 * header matching it by the strongest method, the earlier one on a tie, and
 * leaves the others unmatched. A header that more than one remaining field
 * could claim is never guessed and lands in `ambiguous`.
 */
export function matchHeaders(
  fileHeaders: readonly string[],
  fields: readonly ImportHeaderField[],
  { maxDistanceRatio = DEFAULT_MAX_DISTANCE_RATIO }: MatchHeadersOptions = {},
): HeaderMatch {
  const normalizedFields = fields.map(({ field, aliases = [] }) => ({
    field,
    key: normalizeHeader(field),
    aliases: aliases.map(normalizeHeader),
  }));
  const result: HeaderMatch = {
    ambiguous: {},
    changed: [],
    mapping: {},
    unmatched: [],
  };
  const candidates = fileHeaders.map((header) =>
    findCandidates(normalizeHeader(header), normalizedFields, maxDistanceRatio),
  );
  const openFields = claimFields(candidates);

  for (const [index, header] of fileHeaders.entries()) {
    const open = openFields[index];
    const [field] = open;

    if (open.length > 1) {
      result.ambiguous[header] = open;
    } else if (open.length === 0) {
      result.unmatched.push(header);
    } else {
      result.mapping[header] = field;
      if (header !== field) {
        const { how } = candidates[index];
        result.changed.push({ from: header, how, to: field });
      }
    }
  }

  return result;
}
